<template>
  <div class="navmenu" ref="navMenuEl" :style="navMenuStyle" v-click-outside="closeModal">
    <div class="navmenu-content" :class="contentClass">
      <h4 v-if="title" class="semibold navmenu-title">{{ title }}</h4>
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { Nullable } from '@/types/common';

const props = withDefaults(
  defineProps<{
    title?: string;
    contentClass?: any;
    targetEl?: Nullable<HTMLElement>;
  }>(),
  {
    title: '',
    contentClass: () => [],
    targetEl: () => ({}) as HTMLElement,
  }
);

const emit = defineEmits<{ close: [] }>();

const navMenuStyle = ref({}); // 드롭다운 스타일
const navMenuEl = ref(); // 드롭다운 DOM

// 드롭다운 위치 조정
// getBoundingClientRect()는 viewport 기준 좌표. position:absolute의 기준점은 offsetParent.
// 두 좌표 모두 viewport 기준이므로 차감하면 정확한 relative 좌표를 얻을 수 있다.
// (scrollY 가산 불필요 — sticky/fixed 부모에서는 오히려 오차 발생)
const updatePosition = async () => {
  if (!props.targetEl?.getBoundingClientRect) return;
  await nextTick();
  if (!navMenuEl.value) return;

  const rectTarget = props.targetEl.getBoundingClientRect(); // 버튼 viewport 기준 rect
  const widthNavMenu = navMenuEl.value.getBoundingClientRect().width;

  const parentEl = (navMenuEl.value.offsetParent as HTMLElement) ?? document.body;
  const rectParent = parentEl.getBoundingClientRect(); // offsetParent viewport 기준 rect

  navMenuStyle.value = {
    position: 'absolute',
    top: `${rectTarget.bottom - rectParent.top + 16}px`,
    left: `${rectTarget.right - rectParent.left - widthNavMenu}px`,
  };
};

const closeModal = (event) => {
  if (props.targetEl?.contains(event.target)) {
    return;
  }

  emit('close');
};

onMounted(() => {
  updatePosition();

  window.addEventListener('resize', updatePosition);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updatePosition);
});
</script>

<style scoped lang="scss">
.navmenu {
  position: absolute;
  width: fit-content;
  max-width: calc(100dvw - 3rem);
  z-index: $z-header;
}

.navmenu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem 0.5rem 0.75rem 0.5rem;
  margin-bottom: 0.25rem;
}

.navmenu-content {
  background-color: var(--surface-overlay);
  border: 1px solid var(--border-default);
  min-width: 17rem;
  max-width: 21.25rem;
  max-height: calc(100dvh - 5rem);
  overflow-y: auto;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: $shadow-dropdown;
  &.shareqr-con {
    min-height: 470px;
  }
}

.navmenu-title {
  margin-bottom: 0.5rem;
}

@media (max-width: 480px) {
  .navmenu {
    left: 50% !important;
    transform: translateX(-50%);
    width: 100%;
    .navmenu-content {
      max-width: 100%;
      .body-container {
        justify-content: center;
      }
    }
  }
}
</style>
